import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { setMadadContactModalState } from 'store/state/app/actions';
import { SetMadadContactModalStatePayload } from 'store/state/app/payloads';
import { getOfficeLink, getAgentLink } from 'helpers/contact';
import { AgentCard } from './AgentCard';
import { MadadAgentCard } from '../entities';
import withEvents from 'analytics/withEvents';
import { useIntersectionObserver } from 'hooks/useIntersectionObserver';
import * as actions from 'store/state/router/actions';
import { NavigateToFn, Route } from 'config/routes';


interface WithDispatchProps {
  openContactModal: (payload: SetMadadContactModalStatePayload) => void;
  onCardClickEvent?: () => void;
  sectionInView?: boolean;
  rootNode?: React.MutableRefObject<HTMLDivElement>;
  onCardExposeEvent?: (id: string, officeId: string) => void;
  navigateTo: NavigateToFn;
}

export const AgentSmartCard: React.FC<MadadAgentCard & WithDispatchProps> = props => {
  const {
    id,
    phone,
    agentName,
    agencyId,
    agencyName,
    agencyImageUrl,
    avatarImage,
    openContactModal,
    isRegistered,
    onCardClickEvent,
    verifiedIconColor,
    sectionInView,
    rootNode,
    onCardExposeEvent,
    navigateTo,
  } = props;

  const [ isVisible, setIsVisible ] = useState(false);
  const cardItem = useRef<HTMLDivElement>(null);

  useIntersectionObserver(([ entry ]) => {
    setIsVisible(entry.isIntersecting);
  }, [ cardItem ], { threshold: 0, root: rootNode });

  useEffect(() => {
    if (isVisible && sectionInView) {
      onCardExposeEvent(id, agencyId);
    }
  }, [ onCardExposeEvent, isVisible, sectionInView, id, agencyId ]);

  const href = getAgentLink(id);

  const handleContactClick = () => {
    openContactModal({
      id,
      phone,
      isAgent: true,
      agentName,
      avatarImage,
      avatarHref: href,
      officeId: agencyId,
      officeName: agencyName,
      officeLogoUrl: agencyImageUrl,
      officeHref: getOfficeLink(agencyId),
      eventSource: 'madad_card',
    });
  };

  // using click handler instead of link to avoid "<a> cannot appear as a descendant of <a>.", since phone is already a link
  const handleCardClick = () => {
    if (isRegistered) {
      onCardClickEvent();
      navigateTo(Route.AgentPage, { id, source: 'madad_index' });
    }
  };

  return (
    <div data-auto="agent-link" onClick={handleCardClick} ref={cardItem}>
      <AgentCard
        {...props}
        onContactClick={handleContactClick}
        verifiedIconColor={verifiedIconColor}
      />
    </div>
  );
};

const mapDispatchToProps = {
  openContactModal: setMadadContactModalState,
  navigateTo: actions.navigateTo,
};


const AgentSmartCardWithEvents = withEvents<MadadAgentCard & WithDispatchProps>((sendEvent, props) => ({
  onCardClickEvent: () => {
    sendEvent('madad_card_click', 'madad', {
      event: {
        agent_details: {
          id: props.id,
          office_id: props.agencyId,
        },
      },
    });
  },
  onCardExposeEvent: (id: string, officeId: string) => {
    sendEvent('madad_card_agent_expose', 'madad', {
      event: {
        agent_details: {
          id,
          office_id: officeId,
        },
      },
    });
  },
  openContactModal: (params: SetMadadContactModalStatePayload) => {
    sendEvent('user_contact_me_form_open', 'user', {
      event: {
        agent_details: {
          id: params.id,
          name: params.agentName,
          office_id: params.officeId,
          office_name: params.officeName,
          phone_number: params.phone,
        },
        source: 'madad_card',
      },
    });
  },
}))(AgentSmartCard);

export default connect(null, mapDispatchToProps)(AgentSmartCardWithEvents);
